/**
 * @file Sélecteur de pays pour PhoneInput, robuste à toute imbrication.
 * @module packages.common.components.phone-input.CountrySelect
 *
 * Implémentation volontairement non-modale (ni Drawer ni Modal) :
 * desktop comme mobile rendent un Popper / Portal au-dessus de l'UI sans
 * focus-trap interne. Cela permet d'utiliser le composant à l'intérieur
 * d'une Dialog, d'un autre Drawer ou d'un Popover sans déclencher de
 * conflit de focus-trap ou de gestion `aria-hidden` qui figerait le parent.
 */

import React, {useState, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  InputAdornment,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Portal,
  Fade,
  Slide,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  ClickAwayListener,
  Divider,
} from '@mui/material';
import {getCountryCallingCode} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';
import flags from 'react-phone-number-input/flags';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Pays communs pour chargement rapide (liste prioritaire affichée en premier)
 */
const COMMON_COUNTRIES = ['BE', 'FR', 'LU', 'NL', 'DE', 'GB', 'ES', 'IT'];

/**
 * Z-index unique et élevé pour passer au-dessus de tout Modal parent
 * (MUI Modal = 1300). Aligné desktop et mobile pour cohérence.
 */
const OVERLAY_ZINDEX = 1500;

/**
 * Composant de sélection de pays avec support Desktop/Mobile
 *
 * @param {Object} props - Propriétés du composant
 * @param {string} props.value - Code pays sélectionné (ISO 3166-1 alpha-2)
 * @param {Function} props.onChange - Callback de changement de pays
 * @return {JSX.Element} Composant de sélection de pays
 */
const CountrySelect = ({value, onChange}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllCountries, setShowAllCountries] = useState(false);

  // Liste complète des pays
  const allCountries = useMemo(() => Object.keys(metadata.countries), []);

  // Pays à afficher selon le contexte
  const countries = useMemo(() => {
    if (searchQuery || showAllCountries) {
      return allCountries;
    }
    return COMMON_COUNTRIES.filter((c) => allCountries.includes(c));
  }, [searchQuery, showAllCountries, allCountries]);

  // Filtrage selon la recherche
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;

    const query = searchQuery.toLowerCase();
    return countries.filter((c) => {
      const code = getCountryCallingCode(c);
      return c.toLowerCase().includes(query) || code.includes(query);
    });
  }, [searchQuery, countries]);

  // Organisation des pays
  const sortedCountries = useMemo(() => {
    if (searchQuery) return filteredCountries;

    if (!showAllCountries) {
      return COMMON_COUNTRIES.filter((c) => allCountries.includes(c));
    }

    return [
      ...COMMON_COUNTRIES.filter((c) => allCountries.includes(c)),
      'divider',
      ...allCountries.filter((c) => !COMMON_COUNTRIES.includes(c)),
    ];
  }, [searchQuery, filteredCountries, showAllCountries, allCountries]);

  const handleOpen = () => {
    setOpen(true);
    setSearchQuery('');
    setShowAllCountries(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery('');
  };

  const handleSelect = (country) => {
    onChange(country);
    handleClose();
  };

  const Flag = flags[value];

  // Bouton d'ancrage (clic pour ouvrir)
  const SelectButton = (
    <Box
      ref={anchorRef}
      onClick={handleOpen}
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'gap': 0.75,
        'cursor': 'pointer',
        'minWidth': 90,
        'py': 0.5,
        'px': 1,
        'borderRadius': 1,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {Flag && <Flag style={{width: 24, height: 18}} />}
      <Box component="span" sx={{fontSize: '0.875rem', color: 'text.secondary'}}>
        +{getCountryCallingCode(value)}
      </Box>
      <KeyboardArrowDownIcon sx={{fontSize: '1rem', color: 'text.secondary'}} />
    </Box>
  );

  // Item pays (commun aux deux modes)
  const CountryItem = ({country, onSelect}) => {
    if (country === 'divider') {
      return <Divider sx={{my: 0.5}} />;
    }

    const CountryFlag = flags[country];
    const isCommon = COMMON_COUNTRIES.includes(country);

    return (
      <ListItemButton
        onClick={() => onSelect(country)}
        selected={country === value}
        sx={{py: 1}}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, width: '100%'}}>
          {CountryFlag && <CountryFlag style={{width: 20, height: 15}} />}
          <ListItemText
            primary={country}
            primaryTypographyProps={{
              fontWeight: isCommon ? 500 : 400,
              fontSize: '0.875rem',
            }}
            sx={{flexGrow: 1}}
          />
          <Typography variant="caption" color="text.secondary">
            +{getCountryCallingCode(country)}
          </Typography>
        </Box>
      </ListItemButton>
    );
  };

  CountryItem.propTypes = {
    country: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
  };

  // Contenu interne : recherche + liste (mutualisé desktop/mobile)
  const renderListContent = ({showCloseHeader} = {}) => (
    <>
      {showCloseHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Sélectionner un pays</Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{p: showCloseHeader ? 2 : 1.5, borderBottom: 1, borderColor: 'divider'}}>
        <TextField
          size="small"
          fullWidth
          autoFocus={!isMobile}
          placeholder={showCloseHeader ? 'Rechercher...' : 'Rechercher un pays...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize={showCloseHeader ? 'medium' : 'small'} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List
        sx={{
          flex: showCloseHeader ? 1 : undefined,
          overflow: 'auto',
          maxHeight: showCloseHeader ? undefined : 320,
          py: showCloseHeader ? 1 : 0.5,
        }}
        onScroll={(e) => {
          const element = e.target;
          const threshold = showCloseHeader ? 100 : 50;
          if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
            setShowAllCountries(true);
          }
        }}
      >
        {sortedCountries.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="Aucun pays trouvé"
              primaryTypographyProps={{color: 'text.secondary', align: 'center'}}
            />
          </ListItem>
        ) : (
          <>
            {sortedCountries.map((country) => (
              <CountryItem
                key={country === 'divider' ? 'divider' : country}
                country={country}
                onSelect={handleSelect}
              />
            ))}

            {!showAllCountries && !searchQuery && (
              <ListItemButton
                onClick={() => setShowAllCountries(true)}
                sx={{
                  justifyContent: 'center',
                  color: 'primary.main',
                  fontWeight: 500,
                  borderTop: showCloseHeader ? 0 : 1,
                  borderColor: 'divider',
                  mt: showCloseHeader ? 0 : 0.5,
                  py: showCloseHeader ? 2 : undefined,
                }}
              >
                Voir tous les pays ({allCountries.length})
              </ListItemButton>
            )}
          </>
        )}
      </List>
    </>
  );

  // MODE DESKTOP : Popper ancré au bouton (non-modal)
  if (!isMobile) {
    return (
      <>
        {SelectButton}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{zIndex: OVERLAY_ZINDEX}}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={8}
              sx={{
                width: 320,
                maxHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {renderListContent()}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  // MODE MOBILE : bottom-sheet via Portal (non-modal pour éviter les
  // conflits de focus-trap quand le composant est nesté dans une Dialog).
  return (
    <>
      {SelectButton}
      <Portal>
        <Fade in={open} unmountOnExit>
          <Box
            onClick={handleClose}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: OVERLAY_ZINDEX,
            }}
          />
        </Fade>
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: '90vh',
              zIndex: OVERLAY_ZINDEX + 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRadius: '16px 16px 0 0',
              boxShadow: theme.shadows[16],
              overflow: 'hidden',
            }}
          >
            {renderListContent({showCloseHeader: true})}
          </Box>
        </Slide>
      </Portal>
    </>
  );
};

CountrySelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CountrySelect;
